import { parseAsString, useQueryState } from 'nuqs';
import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { DeploymentsViewRenderer } from './components/DeploymentsViewRenderer';
import DetailsDeployment from './components/DetailsDeployment';
import DetailsPromote from './components/DetailsPromote';
import Header from './components/Header/Header';
import ModalRollback from './components/ModalRollback';
import SideBar from './components/SideBar/SideBar';
import SideBarLoading from './components/SideBarLoading';
import SideSheetDeployPipeline from './components/SideSheetDeployPipeline/SideSheetDeployPipeline';
import {
  createHeaderActionsConfig,
  createPathsConfig
} from './config/headerConfig';
import { DeploymentsProvider } from './context/DeploymentsContext';
import { useCreateDeploy } from './hooks/useCreateDeploy';
import { useDeploymentData } from './hooks/useDeploymentData';
import { useDeploymentModals } from './hooks/useDeploymentModals';
import { useDeploymentNavigation } from './hooks/useDeploymentNavigation';
import { useRedeployDeploy } from './hooks/useRedeployDeploy';
import { useRemoveDeploy } from './hooks/useRemoveDeploy';
import { useDeployUiStore } from './stores/deployUiStore';
import { DeploymentSelect } from './Types';

import apiRun from '~/api/runtime';
import capitalizeFirstLetter from '~/common/helpers/capitalizeFirstLetter';
import i18n from '~/common/helpers/i18n';
import { buildMonitorExecutionFinishUrl } from '~/common/helpers/url/monitorPaths';
import { useTriggers } from '~/common/hooks/useTriggers';
import { useHeader } from '~/components/HeaderProvider';
import { Pipeline } from '~/entities/Pipeline';

export function Deployments() {
  const {
    activeRealm,
    environment,
    environments,
    projectById,
    projectAllIds,
    fetchProjectLoading,
    token
  } = useSelector((state: any) => ({
    activeRealm: state.application.activeRealm,
    environment: state.application.environment,
    environments: state.application.environments.allNames,
    projectById: state.project?.entities?.project,
    projectAllIds: state.project.result.project,
    fetchProjectLoading: state.project?.loading?.fetch,
    token: state?.authentication?.userData?.token
  }));

  const sideSheetOpen = useDeployUiStore(state => state.isDeploySheetOpen);
  const deploymentDetails = useDeployUiStore(state => state.deploymentDetails);
  const setDeploySheetOpen = useDeployUiStore(
    state => state.setDeploySheetOpen
  );
  const startRedeploy = useDeployUiStore(state => state.startRedeploy);
  const showDeploymentDetails = useDeployUiStore(
    state => state.showDeploymentDetails
  );
  const resetDeployUi = useDeployUiStore(state => state.reset);

  const projects = {
    ALL: {
      id: null,
      name: i18n.t('action.all_projects')
    },
    ...projectById
  };

  const dispatch = useDispatch();
  const showBetaCreateDeploy = setDeploySheetOpen;
  const setPipeline = startRedeploy;
  const findEnvironments = useCallback(
    (params: unknown) => dispatch.application.findEnvironments(params),
    [dispatch]
  );
  const fetchProject = useCallback(
    (params: unknown, options: unknown) =>
      dispatch.project.fetch(params, options),
    [dispatch]
  );
  const reset = resetDeployUi;

  const triggers = useTriggers(activeRealm);

  const [outdatedAccountPipelineName] = useQueryState(
    'outdatedAccountPipelineName',
    parseAsString
  );
  const [outdatedAccountPipelineVersion] = useQueryState(
    'outdatedAccountPipelineVersion',
    parseAsString
  );

  const {
    viewParam,
    projectIdParam,
    changeObjSearch,
    setProjectParam,
    changeEnvironment
  } = useDeploymentNavigation();

  const effectiveProjectId = projectIdParam || null;

  const {
    deploymentsList,
    loading,
    refreshWithAbort,
    setDefaultCurrentPage,
    onPageChange,
    onInfinityScroll,
    DEPLOYMENTS_PER_PAGE
  } = useDeploymentData({
    activeRealm,
    environment,
    projectById
  });

  const { createDeploy, creating } = useCreateDeploy();
  const { redeployDeploy, redeploying } = useRedeployDeploy();
  const { removeDeploy } = useRemoveDeploy();
  const loadingCreate = creating || redeploying;

  const remove = useCallback(
    async (deployment: {
      realm: string;
      deployment: string;
      environment: string;
    }) => {
      await removeDeploy(deployment);
      await refreshWithAbort(0);
    },
    [removeDeploy, refreshWithAbort]
  );

  const create = useCallback(
    async (payload: any, _meta: unknown, useV2Api = false) => {
      if (payload?.type === 'redeploy') {
        await redeployDeploy(payload);
      } else {
        await createDeploy({ payload, useV2Api });
      }
      await refreshWithAbort(0);
    },
    [createDeploy, redeployDeploy, refreshWithAbort]
  );

  const onShowModalDeployment = showDeploymentDetails;

  const {
    deploymentSelected,
    isOpenDetailPromote,
    isOpenRollback,
    loadingRollback,
    isDeleteRollback,
    openDetailPromote,
    closeDetailPromotion,
    openModalRollbackDeploy,
    closeModalRollback,
    rollbackDeploy,
    handlePromoteConfirm
  } = useDeploymentModals({
    activeRealm,
    environment,
    environments,
    onDataRefresh: () => refreshWithAbort(0),
    create
  });

  useEffect(() => {
    findEnvironments({ realm: activeRealm });
    fetchProject(null, { loading: true });
  }, [activeRealm, findEnvironments, fetchProject]);

  useEffect(() => () => reset(), [reset]);

  const actionsButton = createHeaderActionsConfig(
    environment,
    showBetaCreateDeploy
  );
  const { editPipelinePath } = createPathsConfig(
    activeRealm as string,
    environment
  );

  useHeader(
    {
      configActionButton: actionsButton
    },
    [environment]
  );

  const goToPipeline = useCallback(
    (pipelineId: string) =>
      window.open(
        `/${activeRealm}/design/v2/pipelines/${pipelineId}`,
        '_blank'
      ),
    [activeRealm]
  );

  const onViewExecutions = useCallback(
    (pipelineName: string) => {
      window.open(
        buildMonitorExecutionFinishUrl(activeRealm, pipelineName, environment),
        '_blank'
      );
    },
    [activeRealm, environment]
  );

  const redeploy = useCallback(
    (pipeline: Pipeline) => {
      setPipeline(pipeline);
    },
    [setPipeline]
  );

  const removeDeployment = useCallback(
    async ({
      realm,
      deployment,
      environment: env
    }: {
      realm: string;
      deployment: string;
      environment: string;
    }) => {
      await remove({ realm, deployment, environment: env });
      await refreshWithAbort(0);
    },
    [remove, refreshWithAbort]
  );

  const handleSideBarClick = (projectId: string) => {
    setDefaultCurrentPage();
    setProjectParam(projectId);
  };

  useEffect(() => {
    onPageChange(0);
  }, [environment, viewParam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (outdatedAccountPipelineName && outdatedAccountPipelineVersion) {
      apiRun
        .getDeployByPipelineNameAndVersion({
          realm: activeRealm,
          pipelineName: outdatedAccountPipelineName,
          pipelineVersion: outdatedAccountPipelineVersion,
          environment,
          token
        })
        .then(pipelineInstance => {
          if (pipelineInstance) {
            redeploy(pipelineInstance);
          }
        });
    }
  }, [
    outdatedAccountPipelineName,
    outdatedAccountPipelineVersion,
    activeRealm,
    environment,
    token,
    redeploy
  ]);

  return (
    <>
      <SideSheetDeployPipeline
        open={sideSheetOpen}
        createDeploy={create}
        realm={activeRealm as unknown as string}
        environment={environment}
        triggers={triggers || []}
        loading={loadingCreate}
        onCancel={
          showBetaCreateDeploy ? () => showBetaCreateDeploy(false) : undefined
        }
        editPipelinePath={editPipelinePath}
        find={changeObjSearch}
      />

      <DetailsDeployment
        redeploy={redeploy}
        triggers={triggers || []}
        title={`${i18n.t('scenes.runtime.labels.details_deployment')} -
          ${capitalizeFirstLetter(environment)}`}
        visible={deploymentDetails.visible}
        onCancel={() => onShowModalDeployment({ modalVisible: false, id: '' })}
        deployment={deploymentDetails?.deployment}
        editPipelinePath={editPipelinePath}
      />

      {isOpenDetailPromote && deploymentSelected && (
        <DetailsPromote
          close={closeDetailPromotion}
          deploymentSelected={deploymentSelected}
          realm={activeRealm as string}
          triggers={triggers}
          find={changeObjSearch}
          create={handlePromoteConfirm}
          changeEnvironment={changeEnvironment}
          remove={removeDeployment}
        />
      )}

      {isOpenRollback && (
        <ModalRollback
          // @ts-expect-error - ModalRollback component prop type mismatch
          deployment={deploymentSelected}
          rollbackDeploy={rollbackDeploy}
          closeModalRollback={closeModalRollback}
          loadingRollback={loadingRollback}
          isDeleteRollback={isDeleteRollback}
        />
      )}

      <div className='overflow-y-auto'>
        <div className='relative'>
          <div className='flex h-[calc(100vh-165px)] overflow-hidden mt-[28px]'>
            {fetchProjectLoading ? (
              <SideBarLoading items={[...Array(15)]} />
            ) : (
              <SideBar
                current={effectiveProjectId}
                byId={projects}
                allIds={['ALL', ...projectAllIds]}
                onClick={handleSideBarClick}
              />
            )}
            <div
              id='scrollable-content'
              className='flex w-full flex-col overflow-y-auto overflow-x-hidden ml-2.5 px-6 pb-4 gap-6'
            >
              <Header />

              <DeploymentsProvider
                loading={loading}
                deploymentsList={deploymentsList}
                current={effectiveProjectId}
                environment={environment}
                activeRealm={activeRealm as string}
                onShowModalDeployment={(deployment: DeploymentSelect) => {
                  onShowModalDeployment({
                    visible: true,
                    id: deployment.id,
                    deployment
                  });
                }}
                goToPipeline={goToPipeline}
                remove={removeDeployment}
                redeploy={redeploy}
                openDetailPromote={openDetailPromote}
                openModalRollbackDeploy={openModalRollbackDeploy}
                onViewExecutions={onViewExecutions}
                onPageChange={onPageChange}
                rowsPerPage={DEPLOYMENTS_PER_PAGE}
                onInfinityScroll={onInfinityScroll}
              >
                <DeploymentsViewRenderer view={viewParam} />
              </DeploymentsProvider>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
